import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {StorageService} from './storage.service';
import {environment} from '../../../environments/environment';
import Keycloak from 'keycloak-js';
import {Router} from '@angular/router';

export interface LoginCredentials {
  username: string;
  password: string;
  clientId: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface UserInfo {
  sub: string;
  email: string;
  name: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  roles: string[];
}

export interface LogoutKeycloack {
  username: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<UserInfo | null>(null);
  // private tokenSubject = new BehaviorSubject<string | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private storage: StorageService,
    private keycloack: Keycloak,
  ) { }

  getAccessToken(): string | null {
    return this.storage.getItem('access_token');
  }

  /**
   * Connexion avec nom d'utilisateur et mot de passe
   */
  login(credentials: LoginCredentials): Observable<any> {
    const tokenUrl = environment.tokenUrl;

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    // Conversion en URLSearchParams
    const body = new URLSearchParams({
      'grant_type': 'password',
      'username': credentials.username,
      'password': credentials.password,
      'client_id': 'habilitation'
    }).toString();

    return this.http.post<AuthResponse>(tokenUrl, body, { headers }).pipe(
      tap((response: AuthResponse) => this.handleAuthResponse(response)),
      catchError((error) => this.handleError(error))
    );
  }

  private handleAuthResponse(response: AuthResponse): void {

    // Stocker les tokens
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);

    // Extraire et stocker l'userId du token
    try {
      const userId = this.extractUserIdFromToken(response.access_token);

      if (userId) {
        this.storage.setItem('userId', userId);
      } else {
        console.error('Impossible d\'extraire l\'userId du token');
      }
    } catch (error) {
      console.error('Erreur lors de l\'extraction de l\'userId:', error);
    }
  }

  /**
   * Extrait l'ID utilisateur (sub) du token JWT
   * @param token Le token JWT access_token
   * @returns L'ID utilisateur ou null si extraction impossible
   */
  extractUserIdFromToken(token: string): string | null {
    try {
      const tokenParts = token.split('.');

      if (tokenParts.length !== 3) {
        throw new Error('Format de token JWT invalide');
      }

      const base64Url = tokenParts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const payload = JSON.parse(jsonPayload);

      // Filtrer resource_access avec destructuring
      if (payload.resource_access) {
        // Déstructuration pour exclure les applications système
        const {
          'realm-management': _rm,
          'broker': _b,
          'account': _a,
          ...filteredResourceAccess
        } = payload.resource_access;

        // Sauvegarder dans le storage - CORRECTION ICI
        this.storage.setItem('appsRolesUser', JSON.stringify({
          resource_access: filteredResourceAccess
        }));

        // Convertir en string pour localStorage
        localStorage.setItem("roles-sauvegardes", JSON.stringify(filteredResourceAccess));
      }

      return payload.sub || null;

    } catch (error) {
      console.error('Erreur lors du décodage du token JWT:', error);
      return null;
    }
  }

  /**
   * Vérifier si l'utilisateur est connecté
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiration = payload.exp;
    return Date.now() < expiration * 1000;
  }

  storeTokens(response: AuthResponse): void {
    this.storage.setItem('access_token', response.access_token);
    this.storage.setItem('refresh_token', response.refresh_token);
    const expiresAt = Date.now() + response.expires_in * 1000;
    this.storage.setItem('token_expires_at', expiresAt.toString());
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = this.storage.getItem('refresh_token');

    if (!refreshToken) {
      return throwError(() => new Error('Aucun refresh token disponible.'));
    }

    const tokenUrl = 'http://localhost:8082/api/auth/refresh';

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const body = {
      refreshToken: refreshToken,
      clientId: 'habilitation' // ou dynamique si besoin
    };

    return this.http.post<AuthResponse>(tokenUrl, body, { headers }).pipe(
      tap((response: AuthResponse) => {
        this.storeTokens(response);
      }),
      catchError(error => {
        console.error('Échec du rafraîchissement du token', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Gérer les erreurs
   */
  private handleError(error: any): Observable<never> {
    console.error('Erreur d\'authentification:', error);

    let errorMessage = 'Une erreur s\'est produite';

    if (error.error?.error_description) {
      errorMessage = error.error.error_description;
    } else if (error.error?.error) {
      switch (error.error.error) {
        case 'invalid_grant':
          errorMessage = 'Nom d\'utilisateur ou mot de passe incorrect';
          break;
        case 'invalid_client':
          errorMessage = 'Configuration client invalide';
          break;
        case 'unauthorized_client':
          errorMessage = 'Client non autorisé';
          break;
        default:
          errorMessage = error.error.error;
      }
    } else if (error.status === 401) {
      errorMessage = 'Nom d\'utilisateur ou mot de passe incorrect';
    } else if (error.status === 403) {
      errorMessage = 'Accès refusé';
    } else if (error.status === 0) {
      errorMessage = 'Impossible de se connecter au serveur';
    }

    return throwError(errorMessage)
  }

  logout(): void {

    this.storage.clear();
    this.keycloack.logout({
      redirectUri: window.location.origin,
      logoutMethod: 'GET',
    });
  }

  async logoutKeycloak(username: string): Promise<void> {
    const url = 'https://auth.cineri.sn/api/habilitation/auth/logout';
    const body = { username: "mountagha" };

    // console.log('Début de la déconnexion');

    try {
      const response = await this.http.post<any>(url, body).toPromise();
      // console.log(' Succès:', response);
      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
    }

    // console.log('🏁 Fin de la déconnexion');
  }
}